import { Typography } from '@SBGSports/referee-react';
import { useFocusImport } from '../use-focus-import';
import styles from '../focus-import.module.css';
import { useTranslations } from 'i18n';
import { TagCount, TagImportTable } from '../component/tag-import-table/tag-import-table';
import { useEffect, useMemo, useRef, useState } from 'react';
import { TableSearch } from '../component/table-search';
import { PageAttributeProps } from 'shared/modals/multi-page-modal/types';
import { useMultiPageWizard } from 'shared/modals/multi-page-modal/use-multi-page-wizard';
import { defaultNavigationBtns } from 'shared/modals/multi-page-modal/multi-page-modal';
import mixpanel from 'mixpanel-browser';
import { EVENTS } from 'analytics/analytics-events';
import { useFocusImportSelections } from 'focus/use-focus-import-selections';
import { useFocusStoredOptions } from 'focus/use-focus-stored-options';
import { LEARN_MORE_LINK } from 'focus/constants';

function countTags(tags: string[]): TagCount[] {
    const tagCounts = tags.reduce<{ [key: string]: number }>((acc, tag) => {
        acc[tag] = (acc[tag] || 0) + 1;
        return acc;
    }, {});

    return Object.keys(tagCounts).map((tag) => ({
        tagName: tag,
        tagCount: tagCounts[tag],
    }));
}
export const TagImportScreen: React.FC<PageAttributeProps> = (props) => {
    const { setButtonDetails } = props;

    // Hooks
    const { focusFile } = useFocusImport();
    const { __ } = useTranslations();
    const { focusStoredOptions } = useFocusStoredOptions();
    const { setTagAlias } = useFocusImportSelections();
    const { setHeader } = useMultiPageWizard();

    const tagCounts: TagCount[] = useMemo(() => {
        return countTags(focusFile?.tags.map((t) => t.tagName) ?? []);
    }, [focusFile?.tags]);

    //State
    const [localSelectionState, setLocalSelectionState] = useState<Map<string, string>>(() => {
        const initialSelection = new Map<string, string>();
        tagCounts.forEach((tag) => {
            const alignment = focusStoredOptions.importedTagAlias.get(tag.tagName);
            if (alignment) {
                initialSelection.set(tag.tagName, alignment ?? '');
            }
        });
        return initialSelection;
    });
    const [tableFilter, setTableFilter] = useState('');

    /**
     * Set header and custom button details here on first render to display on parent component
     */
    const executedRef = useRef(false);
    useEffect(() => {
        if (executedRef.current) {
            return;
        }

        mixpanel.track(EVENTS.focus.tagImportStarted, {});
        mixpanel.time_event(EVENTS.focus.tagImportEnded);
        setButtonDetails?.(defaultNavigationBtns(__, localSelectionState.size === 0));
        setHeader(<Typography variant="heading-2">{__('smt_importer.tag_import_title')}</Typography>);
        executedRef.current = true;

        return () => {
            mixpanel.track(EVENTS.focus.tagImportEnded, {});
        };
    }, [__, setHeader, setButtonDetails, localSelectionState.size]);

    return (
        <div className={styles['start-page-body']}>
            <Typography variant="body-1">
                {__('smt_importer.tag_import_description').concat(' ')}
                <a target="window" href={LEARN_MORE_LINK} className={styles['learn_more_link']}>
                    {__('misc.learn_more')}
                </a>
            </Typography>

            <div className={styles['search-and-count']}>
                <TableSearch searchText={tableFilter} setSearchText={setTableFilter} />
                <Typography variant="caption-1">
                    {__('smt_importer.count_of_total_selected', {
                        count: String(localSelectionState.size ?? 0),
                        total: tagCounts.length.toString(),
                    })}
                </Typography>
            </div>
            <TagImportTable
                tags={tagCounts}
                filterText={tableFilter}
                onValueChanged={(tagsToImport) => {
                    setTagAlias(tagsToImport);
                    setLocalSelectionState(tagsToImport);
                    setButtonDetails?.(defaultNavigationBtns(__, tagsToImport.size === 0));
                }}
            />
        </div>
    );
};
